import React, { useCallback, useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import Mousetrap from 'mousetrap';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';

import CrossIcon from '@atlaskit/icon/core/cross';
import Button from '@atlaskit/button/new';
import type { NewCoreIconProps } from '@atlaskit/icon/base-new';
import CommentIcon from '@atlaskit/icon/core/comment';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, Inline, xcss } from '@atlaskit/primitives';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import {
	handleCommentsPanelNavigation,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import {
	calculateElementOffset,
	focusElementAndUpdateTabIndex,
	getFocusableElementFromMarkerRef,
	getSidebarId,
	isFirstTabbableElement,
	isLastTabbableElement,
} from '@confluence/comments-util/entry-points/domUtils.ts';
import { scrollToElementInEditor } from '@confluence/comments-util/entry-points/editorAnnotations.ts';
import { isInPreviewPanel } from '@confluence/preview-panel-api/entry-points/previewPanelUtils.ts';
import {
	useCommentsContentActions,
	useCommentsContentState,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { ReactionsProvider } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import { CommentIconWithUnread } from '@confluence/object-sidebar-components/entry-points/CommentPanelButton.tsx';
import { isMigratedToPanelSystem } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import {
	useCommentsPanelActions,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import {
	SidebarContainer,
	CloseButtonContainer,
	CloseButton,
	TopContainer,
} from '@confluence/inline-comments-common/entry-points/styled.ts';
import type { Comment } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import {
	MIN_PANEL_WIDTH,
	useIsObjectSidebarShown,
	usePanel,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { INLINE_COMMENTS_SHORTCUT } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { useAnnotations } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';

import { useHasMaxHeight } from './hooks/useHasMaxHeight.ts';
import {
	i18n,
	STICKY_COMMENT_VERTICAL_OFFSET_VIEW,
	STICKY_COMMENT_VERTICAL_OFFSET_VIEW_OLD,
} from './inlineCommentConstants.ts';
import { CommentNavigation } from './CommentNavigation.tsx';

export type CommentNavigationOptions = {
	commentData: Comment;
	onNavigationClick: (nextMarkerRef: string) => void;
};

export const handleIntersectionOld = (
	entry: IntersectionObserverEntry,
	setIsSticky: (_isSticky: boolean) => void,
	setSidebarOffset: (_offset: number) => void,
	sidebarOffset: number,
) => {
	if (entry.intersectionRatio === 0) {
		setIsSticky(false);
		setSidebarOffset(sidebarOffset);
	} else {
		setIsSticky(true);
		setSidebarOffset(
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			expValEquals('confluence_fe_fix_sticky_comment_sidebar_position', 'isEnabled', true) &&
				fg('confluence_frontend_remove_duplicate_sidebar_logic')
				? STICKY_COMMENT_VERTICAL_OFFSET_VIEW
				: STICKY_COMMENT_VERTICAL_OFFSET_VIEW_OLD,
		);
	}
};

type CommentSidebarProps = {
	pageId: string;
	onClose: ((hasContentChanged?: boolean) => void) | undefined;
	annotationElement?: HTMLElement | null;
	navigationOptions?: CommentNavigationOptions;
	isEditor?: boolean;
	isViewCommentMode?: boolean;
	children: ReactNode;
	sidebarOffset: number;
	commentId?: string;

	/**
	 * Should scroll comment sidebar into view after mounting?
	 * When creating comment: scroll the entire sidebar into view;
	 * When viewing comment: scroll the first comment into view only to cater for long comment threads.
	 */
	scrollIntoView?: boolean;
	isOpeningMediaCommentFromToolbar?: boolean;
	sidebarEl: React.MutableRefObject<HTMLElement | null>;
	isSticky: boolean;
	annotationId?: string;
	inlineCommentRef?: (node: any) => void;
	nudgeRef?: React.MutableRefObject<HTMLElement | null>;
	isFabricPage?: boolean;
	authorName?: string | null;
	/**
	 * Call this with the scrollable element for the editor popups
	 */
	setPopupsScrollableElement?: (element: HTMLElement | undefined) => void;
	/**
	 * Callback to notify parent when max height styles are applied/removed
	 */
	onMaxHeightApplied?: (hasMaxHeight: boolean) => void;
};

const redesignedViewAllStyle = xcss({
	position: 'absolute',
	right: 'space.500', // This gives us 4px between the X and the comment icon
	top: 'space.150', // This lines us up exactly with X
});

const viewAllContainerStyles = xcss({
	position: 'absolute',
	top: 'space.150',
	left: 'space.075',
	alignItems: 'center',
	justifyContent: 'center',
});

const commentHeaderStyle = xcss({
	flexDirection: 'column',
	display: 'flex',
	marginTop: 'space.400',
});

const commentNewHeaderMargin = xcss({
	marginTop: 'space.500',
});

const shadowElementStyle = {
	height: '4px',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.15) 0%, transparent 100%)',
	width: '100%',
	margin: '0',
	padding: '0',
};

const commentEditorStyle = xcss({
	marginTop: 'space.0',
});

const boxWithMaxHeight = xcss({
	maxHeight: 'calc(100vh - 300px)',
	overflowY: 'auto',
});

const boxWithoutMaxHeight = xcss({
	overflowY: 'visible',
});

const boxWithDynamicMaxHeightAuto = xcss({
	overflowY: 'auto',
});

export const CommentSidebar: FC<CommentSidebarProps> = ({
	pageId,
	onClose,
	annotationElement,
	navigationOptions,
	isEditor,
	isViewCommentMode,
	children,
	sidebarOffset,
	commentId,
	isOpeningMediaCommentFromToolbar,
	sidebarEl,
	annotationId,
	inlineCommentRef = () => {},
	nudgeRef,
	isSticky,
	isFabricPage,
	authorName,
	setPopupsScrollableElement,
	onMaxHeightApplied,
}) => {
	const { showModal } = useDialogs();
	const [oldIsSticky, setOldIsSticky] = useState<boolean>(false);
	const [newSidebarOffset, setSidebarOffset] = useState<number>(sidebarOffset);
	const [showA11yAnnouncement, setShowA11yAnnouncement] = useState(false);
	const [isScrolled, setIsScrolled] = useState<boolean>(false);
	const [isRefSet, setIsRefSet] = useState(false);
	const { commentSidebarContext } = useAnnotations();

	// Compute isMaxHeightEnabledForSticky first to pass to hook
	const isMaxHeightEnabledForSticky =
		expValEquals('confluence_comment_enable_maxheight', 'isEnabled', true) ||
		(isSticky && fg('confluence-fe-comment-temp-max-height-fix'));

	// Compute hasMaxHeight locally and notify parent via callback
	const { hasMaxHeight, dynamicMaxHeight } = useHasMaxHeight({
		sidebarEl,
		isRefSet,
		isFabricPage,
		isSticky,
		annotationElement,
		isEditor,
		sidebarOffset,
		isMaxHeightEnabledForSticky,
	});
	const applyMaxHeightStyles = hasMaxHeight && isMaxHeightEnabledForSticky;

	// Notify parent when max height styles change
	useEffect(() => {
		onMaxHeightApplied?.(!!applyMaxHeightStyles);
	}, [applyMaxHeightStyles, onMaxHeightApplied]);

	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { showCommentsPanel } = useShowCommentsPanel();

	const { setCurrentlySelectedThreadKey } = useCommentsPanelActions();

	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const shouldHideViewAllCommentsButtonForNativeEmbedUiUpdates =
		isEmbeddedConfluence && expValEquals('cc-maui-experiment', 'isEnabled', true);

	useEffect(() => {
		if (fg('confluence_frontend_remove_duplicate_sidebar_logic')) {
			return;
		}

		let offset = 0;

		if (annotationElement && !isEditor) {
			let scrollParent: HTMLElement | null;

			if (isEditor) {
				scrollParent = document.querySelector('.fabric-editor-popup-scroll-parent');
				offset = calculateElementOffset(annotationElement, scrollParent);
			} else {
				/*
				There's two cases here: a view comment and a create comment.
				We need the correct scrollParent for each case.
				Since it's not the editor in this logical branch we should use #content as the scrollParent.
				*/
				scrollParent = document.querySelector('div#content[data-inline-comments-target="true"]');
				if (scrollParent) {
					let annotationOffset = 0;
					if (isViewCommentMode) {
						// If the sidebar is rendering a view comment, we can use the annotationElement directly.
						annotationOffset = calculateElementOffset(annotationElement);
					} else {
						/*
					If the sidebar is rendering a create comment the annotationElement is actually the wrapperDOM
					we need to find the actual highlight instead. We can find this by looking for the mark because
					there will always be a mark before the create comment button is clicked.
					*/
						const actualHiglightElement: HTMLElement | null = scrollParent.querySelector(
							`[data-annotation-draft-mark="true"]`,
						);
						annotationOffset = calculateElementOffset(actualHiglightElement, undefined, true);
					}
					offset = annotationOffset + scrollParent.offsetTop;
				}
			}
			setSidebarOffset(offset);
		}
	}, [annotationElement, isEditor, isViewCommentMode]);

	useEffect(() => {
		// handles media comment scroll
		if (isRefSet && sidebarEl.current && isEditor) {
			if (isOpeningMediaCommentFromToolbar && annotationElement) {
				scrollToElementInEditor({
					targetElement: annotationElement,
					viewingRoomOffset: -150,
				});
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isRefSet,
		annotationElement,
		isOpeningMediaCommentFromToolbar,
		commentId,
		isViewCommentMode,
		isEditor,
	]);

	useEffect(() => {
		let mouseTrap: any;
		let focusTrap: any;

		if (isRefSet && sidebarEl.current) {
			mouseTrap = new Mousetrap(sidebarEl.current);
			// prevent saving via mod+enter from also publishing the page in edit mode
			mouseTrap.bind(INLINE_COMMENTS_SHORTCUT, (event: Event) => {
				event.stopPropagation();
			});

			if (!showA11yAnnouncement) {
				// We need to show this on a timer so the `aria-live` listener will respond with our message
				setTimeout(() => {
					setShowA11yAnnouncement(true);
				}, 100);
			}
		}

		return () => {
			if (mouseTrap) {
				mouseTrap.reset();
			}

			if (focusTrap) {
				focusTrap.deactivate();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRefSet]);

	useEffect(() => {
		if (
			!isRefSet ||
			!isViewCommentMode ||
			isEditor ||
			!sidebarEl.current ||
			!expVal('cc_comments_a11y_auto_focus_view_page_inline_modal', 'isEnabled', false)
		) {
			return;
		}
		// capture current val to use for cleanup down the line
		const sidebarNode = sidebarEl.current;

		// Both options are important here - by default focus scroll the browser, which can be jarring on clicks
		// We also prevent focus from shifting a lot when clicking buttons for legacy browser support, which causes unexpected focus:visible styling
		// Better for now to prevent it entirely, than to handle each one-off case
		focusElementAndUpdateTabIndex(sidebarNode, {
			focusVisible: false,
			preventScroll: true,
		} as FocusOptions);

		if (
			!annotationId ||
			!expVal('cc_comments_a11y_return_focus_to_vew_annotation', 'isEnabled', false)
		) {
			return;
		}

		// This must happen on keydown instead of focusout. By the time focusout fires,
		// pointer interactions may already be moving focus to another annotation, and restoring
		// focus from the old sidebar can bounce the user back to the previously open thread.
		// Generally speaking, we also can't trust existing focus logic, so can't rely on focus:visible
		// Handling the boundary Tab / Shift+Tab cases here keeps the restore path keyboard-only.
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key !== 'Tab') {
				return;
			}

			const activeElement = document.activeElement;
			if (!(activeElement instanceof HTMLElement) || !sidebarNode.contains(activeElement)) {
				return;
			}

			const isLeavingBackwards =
				e.shiftKey &&
				(activeElement === sidebarNode || isFirstTabbableElement(sidebarNode, activeElement));
			const isLeavingForwards = !e.shiftKey && isLastTabbableElement(sidebarNode, activeElement);

			if (!isLeavingBackwards && !isLeavingForwards) {
				return;
			}

			const annotationEl = getFocusableElementFromMarkerRef(annotationId);
			if (!annotationEl) {
				return;
			}

			e.preventDefault();

			// Use the default browser focus when leaving the modal, since we don't need
			// to update tabIndex here.
			annotationEl.focus();
		};

		sidebarNode.addEventListener('keydown', handleKeyDown, true);
		return () => {
			sidebarNode.removeEventListener('keydown', handleKeyDown, true);
		};
		// No need to have sidebarEL in here - handled by isRefSet
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRefSet, isViewCommentMode, isEditor, annotationId]);

	const handlePropagation = (event: any) => {
		/**
		 * prevents an event bubbling up to removing focus
		 * from the highlight displaying the sidebar
		 */
		event.stopPropagation();
	};

	const handleOpeningCommentsPanel = useCallback(() => {
		showCommentsPanel({ openWithView: ViewValues.OPEN });
		if (annotationId) {
			setCurrentlySelectedThreadKey(annotationId);
			void handleCommentsPanelNavigation({
				scrollBehavior: ScrollBehavior.SMOOTH,
				context: NavigationContext.PANEL,
				block: 'center',
				annotationId,
				commentId,
				commentType: 'INLINE',
				annotationClicked: true,
			});
		}
	}, [showCommentsPanel, setCurrentlySelectedThreadKey, annotationId, commentId]);

	const handleOnClose = useCallback(
		(dialogs: DialogsStateContainer, isOpeningCommentsPanel: boolean = false) => {
			if (hasContentChanged) {
				dialogs.showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged();
						isOpeningCommentsPanel ? handleOpeningCommentsPanel() : onClose && onClose(false);
					},
				});
			} else {
				isOpeningCommentsPanel
					? handleOpeningCommentsPanel()
					: onClose && onClose(hasContentChanged);
			}
		},
		[hasContentChanged, onClose, resetContentChanged, handleOpeningCommentsPanel],
	);

	const updatedHandleOnClose = useCallback(
		(isOpeningCommentsPanel: boolean = false) => {
			if (hasContentChanged) {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged();
						isOpeningCommentsPanel ? handleOpeningCommentsPanel() : onClose?.(false);
					},
				});
			} else {
				isOpeningCommentsPanel ? handleOpeningCommentsPanel() : onClose?.(hasContentChanged);
			}
		},
		[showModal, hasContentChanged, onClose, resetContentChanged, handleOpeningCommentsPanel],
	);

	const handleViewAllClick = useCallback(
		(dialogs: DialogsStateContainer) => {
			handleOnClose(dialogs, true);
		},
		[handleOnClose],
	);

	const updatedHandleViewAllClick = useCallback(() => {
		updatedHandleOnClose(true);
	}, [updatedHandleOnClose]);

	const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		// Check if the inline-comment-sidebar-wrapper is scrolled
		const scrolled = target.scrollTop > 0;
		setIsScrolled(scrolled);
	}, []);

	const sidebarId = getSidebarId(isEditor, isViewCommentMode);

	const mode = isEditor ? 'edit' : 'view';

	const intl = useIntl();
	const closeCommentBox = intl.formatMessage(i18n.closeCommentBox);

	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();

	const sidebarBoundingRect = sidebarEl.current?.getBoundingClientRect();
	const isSidebarExtendedBeyondViewport = (sidebarBoundingRect?.bottom ?? 0) > window.innerHeight;

	/**
	 * useEffect to handle sticky comment behavior when attached to a sticky table header.
	 * As the user scrolls, the IntersectionObserver triggers the callback the moment the table header starts/stops being sticky
	 */
	useEffect(() => {
		if (!fg('confluence_frontend_remove_duplicate_sidebar_logic')) {
			const stickyHeader = annotationElement?.closest('.pm-table-sticky-wrapper');

			if (stickyHeader && !isSidebarExtendedBeyondViewport) {
				// eslint-disable-next-line no-restricted-syntax
				const observer = new IntersectionObserver(
					([entry]) =>
						handleIntersectionOld(entry, setOldIsSticky, setSidebarOffset, sidebarOffset),
					{ threshold: 1.0 },
				);
				observer.observe(stickyHeader);

				return () => observer.disconnect();
			}
		}
	}, [annotationElement, sidebarOffset, sidebarEl, isSidebarExtendedBeyondViewport]);

	const commentSidebarIsSticky = fg('confluence_frontend_remove_duplicate_sidebar_logic')
		? isSticky
		: oldIsSticky;

	const commentResidesInPreviewPanel = fg('confluence_comment_enable_preview_panel_support')
		? isInPreviewPanel()
		: false;

	const viewAllResidesInPreviewPanel = fg('confluence_previewpanel_enable_comments_panel')
		? isInPreviewPanel()
		: false;

	/**
	 * Below logic to set rightOffset is temporary fix.
	 * It is very fragile as there are many conditions in ObjectSidebarControl
	 * 	component to show/hide action bar and rovo button.
	 * Also values are hardcoded.
	 */
	const getRightOffset = useCallback(() => {
		let rightOffset = 0;
		const panelRenderedOutsideMain =
			isMigratedToPanelSystem(panel?.id) && fg('ads-panel-system-confluence-object-sidebar');
		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			expValEquals('confluence_fe_fix_sticky_comment_sidebar_position', 'isEnabled', true) &&
			fg('confluence_frontend_remove_duplicate_sidebar_logic')
		) {
			// An improved version of getRightOffset()
			if (!commentResidesInPreviewPanel) {
				const baseRightOffset = 112;
				// rendererContent has an extra 40px padding on the right,
				// which we need to add in 'edit' mode to match 'view' mode.
				const extraPadding = 40;
				if (mode === 'edit') {
					rightOffset = extraPadding + (isObjectSidebarShown ? 0 : baseRightOffset);
				} else {
					// `mode === 'view'`
					if (isObjectSidebarShown) {
						if (panelRenderedOutsideMain) {
							// Navigation-system Panel already shrinks Main, so only fixed-position
							// (sticky) inline comments need a viewport-relative offset.
							rightOffset = commentSidebarIsSticky ? MIN_PANEL_WIDTH + extraPadding : 0;
						} else {
							// When `commentSidebarIsSticky === true`, sidebar uses style { position: 'fixed' },
							// so we need to add extraPadding here to match the `commentSidebarIsSticky === false` case.
							rightOffset = MIN_PANEL_WIDTH + (commentSidebarIsSticky ? extraPadding : 0);
						}
					} else {
						// `isObjectSidebarShown === false`
						if (!commentSidebarIsSticky) {
							rightOffset = baseRightOffset;
						} else {
							// `commentSidebarIsSticky === true`, sidebar uses style { position: 'fixed' }
							// We set rightOffset to be baseRightOffset + the distance between
							// the right edge of 'main-content' and the right edge of window
							// (which includes the 40px padding + any extra e.g. the Rovo panel).
							// The resulting horizontal position of sidebar should match the
							// `commentSidebarIsSticky === false` case.
							const rendererContent = document.getElementById('main-content');
							const rendererContentRight = rendererContent?.getBoundingClientRect()?.right ?? 0;
							if (rendererContentRight) {
								const rendererContentDistanceFromWindowRight =
									window.innerWidth - rendererContentRight;
								rightOffset = baseRightOffset + rendererContentDistanceFromWindowRight;
							} else {
								// rendererContentRight === 0, which shouldn't occur, but
								// we fall back to a hardcoded rightOffset
								rightOffset = baseRightOffset;
							}
						}
					}
				}
			} // if `commentResidesInPreviewPanel === true`, rightOffset will be 0
		} else {
			// The old version of getRightOffset()
			if (mode === 'view' && isObjectSidebarShown) {
				/**
				 * Need rightOffset only when container size doesn't change when
				 * 	action bar panel (e.g details panel, comments panel).
				 * That happens currently only in view page.
				 */
				rightOffset = panelRenderedOutsideMain ? 0 : MIN_PANEL_WIDTH;
			} else if (
				/**
				 * When action bar or rovo button is shown, need to set rightOffset,
				 * 	otherwise long comment sidebar overlaps on it.
				 */
				!isObjectSidebarShown &&
				(fg('confluence_frontend_content_wrapper') || isRovoButtonEnabled)
			) {
				/**
				 * Width of action bar is 48px,
				 * 	got it from object-sidebar-components/src/ObjectSidebarControl.tsx.
				 * This is not ideal solution, as it is hardcoded.
				 * check if previewPanel support is enabled
				 */
				if (!commentResidesInPreviewPanel) {
					if (fg('confluence_frontend_remove_duplicate_sidebar_logic')) {
						rightOffset = 100; // add more offset from the right edge of the page so it doesn't overlap with the object sidebar
						if (mode === 'view') {
							rightOffset += commentSidebarIsSticky ? 67 : 12;
						} else {
							rightOffset += commentSidebarIsSticky ? 27 : 12;
						}
					} else {
						rightOffset = 48 + (commentSidebarIsSticky ? 52 : 12);
					}
				}
			}
		}
		return rightOffset;
	}, [
		mode,
		isObjectSidebarShown,
		commentSidebarIsSticky,
		commentResidesInPreviewPanel,
		isRovoButtonEnabled,
		panel,
	]);

	const a11yProps = expVal('confluence_frontend_comments_a11y_kb_exp', 'isEnabled', false)
		? {
				role: 'dialog',
				'aria-modal': false,
				'aria-label': intl.formatMessage(i18n.commentSidebarLabel, { name: authorName || '' }),
			}
		: {};

	const pickedStickyCommentVerticalOffsetView =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		expValEquals('confluence_fe_fix_sticky_comment_sidebar_position', 'isEnabled', true) &&
		fg('confluence_frontend_remove_duplicate_sidebar_logic')
			? STICKY_COMMENT_VERTICAL_OFFSET_VIEW
			: STICKY_COMMENT_VERTICAL_OFFSET_VIEW_OLD;
	const sidebarContainerOffset = fg('confluence_frontend_remove_duplicate_sidebar_logic')
		? commentSidebarIsSticky
			? isEditor
				? sidebarOffset
				: pickedStickyCommentVerticalOffsetView
			: sidebarOffset
		: newSidebarOffset;

	return (
		<ErrorBoundary attribution={Attribution.COLLABORATION}>
			<Subscribe to={[DialogsStateContainer]}>
				{(dialogs: DialogsStateContainer) => (
					<ReactionsProvider contentId={pageId}>
						<UFOSegment name="comment-sidebar">
							{commentSidebarContext === 'create' && (
								<UFOCustomData data={{ commentSidebarContext: 'create' }} />
							)}
							<SidebarContainer
								offset={sidebarContainerOffset}
								rightOffset={getRightOffset()}
								isSticky={commentSidebarIsSticky}
								onClick={handlePropagation}
								id={sidebarId}
								data-testid={sidebarId}
								data-cy={sidebarId}
								mode={mode}
								ref={(el) => {
									sidebarEl.current = el;
									if (nudgeRef) nudgeRef.current = el;
									setIsRefSet(true);
									inlineCommentRef(el);
								}}
								{...a11yProps}
							>
								<>
									{!fg('confluence_frontend_content_wrapper') && (
										<TopContainer showNavigation={Boolean(navigationOptions)}>
											{annotationId && !shouldHideViewAllCommentsButtonForNativeEmbedUiUpdates && (
												<Inline xcss={viewAllContainerStyles} testId="view-all-container">
													<Button
														onClick={
															expValEquals(
																'confluence_frontend_remove_a11y_suppressions',
																'isEnabled',
																true,
															)
																? () => updatedHandleViewAllClick()
																: () => handleViewAllClick(dialogs)
														}
														iconBefore={(iconProps: NewCoreIconProps) => (
															<CommentIcon
																{...iconProps}
																label="Comment Icon"
																color="var(--ds-icon-subtle)"
															/>
														)}
														appearance="subtle"
														spacing="compact"
														testId="comment-sidebar-viewall-button"
														interactionName="view-comments-menu-action"
													>
														<Text color="color.text.subtle">
															{intl.formatMessage(i18n.viewAllCommentsButtonText)}
														</Text>
													</Button>
												</Inline>
											)}
											{navigationOptions && (
												<CommentNavigation
													pageId={pageId}
													commentData={navigationOptions.commentData}
													isEditor={isEditor}
													onNavigationClick={navigationOptions.onNavigationClick}
												/>
											)}
											<Tooltip
												content={<FormattedMessage {...i18n.closeSidebarTooltip} />}
												hideTooltipOnClick
												position="top"
											>
												<CloseButtonContainer>
													<CloseButton
														appearance="subtle"
														isUpdatedStandaloneComment={false}
														onClick={
															expValEquals(
																'confluence_frontend_remove_a11y_suppressions',
																'isEnabled',
																true,
															)
																? () => updatedHandleOnClose()
																: () => handleOnClose(dialogs)
														}
														onMouseDown={(e) => e.preventDefault()}
														testId="close-comment-sidebar-button"
														data-cy={`${isEditor ? 'editor' : 'renderer'}-close-${
															isViewCommentMode ? 'view' : 'create'
														}-comment`}
														iconBefore={
															<CrossIcon
																label={closeCommentBox}
																color={token('color.icon.subtle')}
																spacing="none"
															/>
														}
													/>
												</CloseButtonContainer>
											</Tooltip>
										</TopContainer>
									)}
									{fg('confluence_frontend_content_wrapper') && (
										<>
											<Box
												xcss={[
													commentHeaderStyle,
													commentNewHeaderMargin,
													!isViewCommentMode && commentEditorStyle,
												]}
											>
												{navigationOptions && (
													<CommentNavigation
														pageId={pageId}
														commentData={navigationOptions.commentData}
														isEditor={isEditor}
														onNavigationClick={navigationOptions.onNavigationClick}
													/>
												)}
												{annotationId &&
													!viewAllResidesInPreviewPanel &&
													!shouldHideViewAllCommentsButtonForNativeEmbedUiUpdates && (
														<Inline testId="redesigned-view-all-container">
															{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
															<Box
																xcss={redesignedViewAllStyle}
																onClick={
																	// In order to get rid of the a11y suppression 👆,
																	// we need to move this onClick handler from this Box to CommentIconWithUnread's IconButton.
																	expValEquals(
																		'confluence_frontend_remove_a11y_suppressions',
																		'isEnabled',
																		true,
																	)
																		? undefined
																		: (e: React.MouseEvent) => {
																				e.stopPropagation();
																				handleViewAllClick(dialogs);
																			}
																}
															>
																<CommentIconWithUnread
																	onClick={
																		expValEquals(
																			'confluence_frontend_remove_a11y_suppressions',
																			'isEnabled',
																			true,
																		)
																			? updatedHandleViewAllClick
																			: undefined
																	}
																	contentId={pageId}
																	testId="standalone-comment-view-all-button"
																	isStandaloneComment
																	isViewAllIcon
																/>
															</Box>
														</Inline>
													)}
												<Tooltip
													content={<FormattedMessage {...i18n.closeSidebarTooltip} />}
													hideTooltipOnClick
													position="top"
												>
													<CloseButtonContainer>
														<CloseButton
															appearance="subtle"
															onClick={
																expValEquals(
																	'confluence_frontend_remove_a11y_suppressions',
																	'isEnabled',
																	true,
																)
																	? () => updatedHandleOnClose()
																	: () => handleOnClose(dialogs)
															}
															onMouseDown={(e) => e.preventDefault()}
															testId="close-comment-sidebar-button"
															data-cy={`${isEditor ? 'editor' : 'renderer'}-close-${
																isViewCommentMode ? 'view' : 'create'
															}-comment`}
															iconBefore={
																<CrossIcon
																	label={intl.formatMessage(i18n.closeInlineCommentThread)}
																	color={token('color.icon.subtle')}
																	spacing="none"
																/>
															}
															isUpdatedStandaloneComment
														/>
													</CloseButtonContainer>
												</Tooltip>
											</Box>
											{isScrolled && (
												// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
												<div style={shadowElementStyle} />
											)}
										</>
									)}
								</>
								<Box
									testId="inline-comment-sidebar-wrapper"
									ref={(el: HTMLElement) => {
										if (applyMaxHeightStyles) {
											setPopupsScrollableElement?.(el);
										}
									}}
									xcss={[
										applyMaxHeightStyles && !dynamicMaxHeight && boxWithMaxHeight,
										applyMaxHeightStyles && !!dynamicMaxHeight && boxWithDynamicMaxHeightAuto,
										!applyMaxHeightStyles && boxWithoutMaxHeight,
									]}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
									style={
										// xcss only supports static styles
										applyMaxHeightStyles && !!dynamicMaxHeight
											? { maxHeight: dynamicMaxHeight }
											: undefined
									}
									onScroll={handleScroll}
								>
									{children}
								</Box>
							</SidebarContainer>
						</UFOSegment>
					</ReactionsProvider>
				)}
			</Subscribe>
		</ErrorBoundary>
	);
};
